/**
 * MasterCrewReport.jsx — Admin-only cross-tech filterable views
 * Browning Electric / ElecBid
 *
 * Lives inside the Crew tab as a fourth sub-tab, gated by the
 * view_master_crew_report permission. Where the Dashboard / Individual
 * Details tabs aggregate per tech, this view lets admins search and
 * filter across every crew member's records — useful for spot-checks,
 * audits, and ad-hoc questions ("show me Tommy's late timesheets last
 * month, sorted by hours").
 *
 * Internal sub-tabs:
 *   - Timesheets    — every timesheet, with on-time / forgot flags
 *   - Notes         — every job note logged by a tech
 *   - Profitability — one row per closed job (lead tech, profit, bonus)
 *   - Reviews       — every Google review with credited techs
 *
 * Data: receives already-enriched arrays from CrewPerformance so this
 * component doesn't need to re-implement helpers like parseDate,
 * firstName, canonical name resolution, etc.
 */

import { useState, useMemo } from "react";

// ─── Brand colours (mirrors CrewPerformance) ────────────────────────────────
// TODO: extract to a shared theme file. For now, duplicating to keep this
// component self-contained.
const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a3140",
  accent: "#a5ce3a", accentDim: "rgba(165,206,58,0.12)",
  green: "#22c55e", greenDim: "rgba(34,197,94,0.10)",
  yellow: "#eab308", yellowDim: "rgba(234,179,8,0.10)",
  red: "#ef4444", redDim: "rgba(239,68,68,0.10)",
  blue: "#3b82f6", blueDim: "rgba(59,130,246,0.10)",
  purple: "#a78bfa", purpleDim: "rgba(167,139,250,0.10)",
  text: "#f0f4f8", muted: "#8892a4", dim: "#4a5568",
};

const ROW_LIMIT = 500;

const SUB_TABS = [
  { id: "timesheets",    label: "Timesheets" },
  { id: "notes",         label: "Notes" },
  { id: "profitability", label: "Profitability" },
  { id: "reviews",       label: "Reviews" },
];

// ─── Date-range helper ──────────────────────────────────────────────────────
function getDateRange(preset) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth(), dow = now.getDay();
  switch (preset) {
    case "this_week": {
      const mon = new Date(y, m, now.getDate() - ((dow + 6) % 7));
      mon.setHours(0, 0, 0, 0);
      return { from: mon, to: now };
    }
    case "last_week": {
      const mon = new Date(y, m, now.getDate() - ((dow + 6) % 7) - 7);
      mon.setHours(0, 0, 0, 0);
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6); sun.setHours(23, 59, 59, 999);
      return { from: mon, to: sun };
    }
    case "this_month":   return { from: new Date(y, m, 1), to: now };
    case "last_month":   return { from: new Date(y, m - 1, 1), to: new Date(y, m, 0, 23, 59, 59) };
    case "last_30":      return { from: new Date(y, m, now.getDate() - 30), to: now };
    case "last_90":      return { from: new Date(y, m, now.getDate() - 90), to: now };
    case "this_quarter": return { from: new Date(y, Math.floor(m / 3) * 3, 1), to: now };
    case "this_year":    return { from: new Date(y, 0, 1), to: now };
    case "all":          return { from: null, to: null };
    default:             return { from: null, to: null };
  }
}

// ─── Formatting helpers ─────────────────────────────────────────────────────
const fmtDate = (d) => d ? d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "--";
const fmtTime = (d) => d ? d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit", hour12: true }) : "--";
const dollar  = (n) => n != null ? `$${Number(n).toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : "--";
const pct     = (n) => n != null ? `${Number(n).toFixed(1)}%` : "--";

// ─── Shared styles ──────────────────────────────────────────────────────────
const selectStyle = {
  padding: "8px 10px", fontSize: 13, background: C.card, color: C.text,
  border: `1px solid ${C.border}`, borderRadius: 6, cursor: "pointer",
  minWidth: 140,
};
const inputStyle = {
  padding: "8px 10px", fontSize: 13, background: C.card, color: C.text,
  border: `1px solid ${C.border}`, borderRadius: 6, minWidth: 220, flex: 1,
};
const tdStyle = {
  padding: "10px 12px", fontSize: 13, borderBottom: `1px solid ${C.border}`,
  color: C.text, whiteSpace: "nowrap",
};

function makeThStyle(sortCol) {
  return (col) => ({
    padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700,
    color: sortCol === col ? C.accent : C.muted,
    cursor: col ? "pointer" : "default",
    whiteSpace: "nowrap",
    textTransform: "uppercase", letterSpacing: "0.06em",
    borderBottom: `1px solid ${C.border}`, background: C.surface,
    position: "sticky", top: 0, zIndex: 2,
    userSelect: "none",
  });
}
const sortArrow = (sortCol, sortDir, col) =>
  sortCol === col ? (sortDir === "asc" ? " ↑" : " ↓") : "";

// ─── Common date preset dropdown options ────────────────────────────────────
const DATE_PRESET_OPTIONS = [
  { value: "last_30",      label: "Last 30 Days" },
  { value: "last_90",      label: "Last 90 Days" },
  { value: "this_week",    label: "This Week" },
  { value: "last_week",    label: "Last Week" },
  { value: "this_month",   label: "This Month" },
  { value: "last_month",   label: "Last Month" },
  { value: "this_quarter", label: "This Quarter" },
  { value: "this_year",    label: "This Year" },
  { value: "all",          label: "All Time" },
];

// ════════════════════════════════════════════════════════════════════════════
// Top-level — sub-tab bar + dispatch
// ════════════════════════════════════════════════════════════════════════════
export default function MasterCrewReport({
  enrichedTimesheets,
  enrichedNotes,
  enrichedReviews,
  enrichedJobs,
  allTechNames,
}) {
  const [subTab, setSubTab] = useState("timesheets");

  return (
    <div style={{ padding: "16px 20px" }}>
      {/* ── Sub-tab bar ───────────────────────────────────────────── */}
      <div style={{
        display: "flex", gap: 4, marginBottom: 16,
        borderBottom: `1px solid ${C.border}`,
      }}>
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            style={{
              padding: "10px 16px", border: "none", cursor: "pointer",
              background: "transparent",
              color: subTab === t.id ? C.text : C.muted,
              fontSize: 13, fontWeight: subTab === t.id ? 600 : 400,
              borderBottom: subTab === t.id ? `2px solid ${C.accent}` : "2px solid transparent",
              marginBottom: -1,
              transition: "all 0.15s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "timesheets" && (
        <TimesheetsMaster enrichedTimesheets={enrichedTimesheets} allTechNames={allTechNames} />
      )}
      {subTab === "notes" && (
        <NotesMaster enrichedNotes={enrichedNotes} allTechNames={allTechNames} />
      )}
      {subTab === "profitability" && (
        <ProfitabilityMaster enrichedJobs={enrichedJobs} allTechNames={allTechNames} />
      )}
      {subTab === "reviews" && (
        <ReviewsMaster enrichedReviews={enrichedReviews} allTechNames={allTechNames} />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TIMESHEETS MASTER VIEW — flat table with filters + sort + KPIs
// ════════════════════════════════════════════════════════════════════════════
function TimesheetsMaster({ enrichedTimesheets, allTechNames }) {
  const [techFilter, setTechFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("last_30");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const dateRange = useMemo(() => getDateRange(datePreset), [datePreset]);

  const filtered = useMemo(() => {
    let result = enrichedTimesheets;

    if (dateRange.from) result = result.filter(ts => ts._startDate && ts._startDate >= dateRange.from);
    if (dateRange.to)   result = result.filter(ts => ts._startDate && ts._startDate <= dateRange.to);

    if (techFilter !== "all") {
      result = result.filter(ts => ts._techName === techFilter);
    }

    if (statusFilter === "on_time")    result = result.filter(ts => ts._isOnTime === true);
    else if (statusFilter === "late")  result = result.filter(ts => ts._isOnTime === false);
    else if (statusFilter === "forgot") result = result.filter(ts => ts._isForgotSignOut);

    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(ts =>
        (ts._clientName || "").toLowerCase().includes(q) ||
        (ts.job_title || "").toLowerCase().includes(q) ||
        (ts._jobNum || "").includes(q) ||
        (ts._techName || "").toLowerCase().includes(q)
      );
    }

    return result;
  }, [enrichedTimesheets, dateRange, techFilter, statusFilter, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av, bv;
      switch (sortCol) {
        case "tech":     av = a._techName || ""; bv = b._techName || ""; break;
        case "date":     av = a._startDate?.getTime() || 0; bv = b._startDate?.getTime() || 0; break;
        case "client":   av = (a._clientName || a.job_title || "").toLowerCase();
                         bv = (b._clientName || b.job_title || "").toLowerCase(); break;
        case "hours":    av = a.duration_hours || 0; bv = b.duration_hours || 0; break;
        case "entered":  av = a._daysLate ?? -1; bv = b._daysLate ?? -1; break;
        default:         return 0;
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortCol, sortDir]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  }

  const onTimeCount = filtered.filter(ts => ts._isOnTime === true).length;
  const lateCount   = filtered.filter(ts => ts._isOnTime === false).length;
  const forgotCount = filtered.filter(ts => ts._isForgotSignOut).length;
  const totalHours  = filtered.reduce((s, ts) => s + (ts.duration_hours || 0), 0);
  const onTimeRate  = (onTimeCount + lateCount) > 0
    ? (onTimeCount / (onTimeCount + lateCount)) * 100
    : null;

  const visible = sorted.slice(0, ROW_LIMIT);
  const truncated = sorted.length > ROW_LIMIT;
  const thStyle = makeThStyle(sortCol);

  return (
    <div>
      <FilterRow>
        <select value={techFilter} onChange={e => setTechFilter(e.target.value)} style={selectStyle}>
          <option value="all">All Techs</option>
          {allTechNames.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <DatePresetSelect value={datePreset} onChange={setDatePreset} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="all">All Status</option>
          <option value="on_time">On Time Only</option>
          <option value="late">Late Only</option>
          <option value="forgot">Forgot Sign-Out</option>
        </select>
        <input
          type="text"
          placeholder="Search client, job title, #, tech…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={inputStyle}
        />
      </FilterRow>

      <KPIStrip>
        <KPI label="Timesheets"      value={filtered.length.toLocaleString()} />
        <KPI label="Total Hours"     value={totalHours.toFixed(1)} />
        <KPI
          label="On-Time Rate"
          value={onTimeRate != null ? `${onTimeRate.toFixed(1)}%` : "--"}
          color={onTimeRate == null ? C.muted : onTimeRate >= 90 ? C.green : onTimeRate >= 70 ? C.yellow : C.red}
        />
        <KPI label="Late Entries"    value={lateCount.toLocaleString()}   color={lateCount > 0 ? C.yellow : C.muted} />
        <KPI label="Forgot Sign-Out" value={forgotCount.toLocaleString()} color={forgotCount > 0 ? C.red : C.muted} />
      </KPIStrip>

      <TruncationBanner truncated={truncated} total={sorted.length} />

      <TableShell>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr>
              <th style={thStyle("tech")}    onClick={() => toggleSort("tech")}>Tech{sortArrow(sortCol, sortDir, "tech")}</th>
              <th style={thStyle("date")}    onClick={() => toggleSort("date")}>Date{sortArrow(sortCol, sortDir, "date")}</th>
              <th style={thStyle("client")}  onClick={() => toggleSort("client")}>Client / Job{sortArrow(sortCol, sortDir, "client")}</th>
              <th style={{ ...thStyle("hours"), textAlign: "right" }} onClick={() => toggleSort("hours")}>Hours{sortArrow(sortCol, sortDir, "hours")}</th>
              <th style={thStyle()}>Clock In</th>
              <th style={thStyle()}>Clock Out</th>
              <th style={thStyle()}>Status</th>
              <th style={thStyle("entered")} onClick={() => toggleSort("entered")}>Entered{sortArrow(sortCol, sortDir, "entered")}</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && <EmptyRow colSpan={8} message="No timesheets match these filters." />}
            {visible.map(ts => {
              const enteredLabel =
                ts._isOnTime === true ? "Same day" :
                ts._isOnTime === false ? `${ts._daysLate}d late` :
                "—";
              const enteredColor =
                ts._isOnTime === true ? C.green :
                ts._isOnTime === false ? (ts._daysLate > 3 ? C.red : C.yellow) :
                C.muted;
              return (
                <tr key={ts.id} style={{ background: C.bg }}>
                  <td style={{ ...tdStyle, color: C.accent, fontWeight: 600 }}>{ts._techName}</td>
                  <td style={tdStyle}>{fmtDate(ts._startDate)}</td>
                  <td style={{ ...tdStyle, whiteSpace: "normal", maxWidth: 260 }}>
                    <ClientJobCell clientName={ts._clientName} jobTitle={ts.job_title} jobNum={ts._jobNum} />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {(ts.duration_hours || 0).toFixed(1)}
                  </td>
                  <td style={{ ...tdStyle, color: C.muted, fontVariantNumeric: "tabular-nums" }}>{fmtTime(ts._startDate)}</td>
                  <td style={{ ...tdStyle, color: C.muted, fontVariantNumeric: "tabular-nums" }}>{fmtTime(ts._endDate)}</td>
                  <td style={tdStyle}>
                    {ts._isForgotSignOut && <Pill color={C.red}    bg={C.redDim}>Forgot</Pill>}
                    {ts._isOnTime === true  && <Pill color={C.green}  bg={C.greenDim}>On Time</Pill>}
                    {ts._isOnTime === false && <Pill color={C.yellow} bg={C.yellowDim}>Late</Pill>}
                  </td>
                  <td style={{ ...tdStyle, color: enteredColor, fontVariantNumeric: "tabular-nums" }}>
                    {enteredLabel}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableShell>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// NOTES MASTER VIEW
// ════════════════════════════════════════════════════════════════════════════
function NotesMaster({ enrichedNotes, allTechNames }) {
  const [techFilter, setTechFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("last_30");
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const dateRange = useMemo(() => getDateRange(datePreset), [datePreset]);

  const filtered = useMemo(() => {
    let result = enrichedNotes;
    if (dateRange.from) result = result.filter(n => n._createdDate && n._createdDate >= dateRange.from);
    if (dateRange.to)   result = result.filter(n => n._createdDate && n._createdDate <= dateRange.to);
    if (techFilter !== "all") result = result.filter(n => n._techName === techFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(n =>
        (n._message || "").toLowerCase().includes(q) ||
        (n._clientName || "").toLowerCase().includes(q) ||
        (n._jobNum || "").includes(q) ||
        (n._techName || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [enrichedNotes, dateRange, techFilter, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av, bv;
      switch (sortCol) {
        case "tech":   av = a._techName || ""; bv = b._techName || ""; break;
        case "date":   av = a._createdDate?.getTime() || 0; bv = b._createdDate?.getTime() || 0; break;
        case "client": av = (a._clientName || "").toLowerCase(); bv = (b._clientName || "").toLowerCase(); break;
        default:       return 0;
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortCol, sortDir]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  }

  // KPIs
  const totalNotes = filtered.length;
  const days = (dateRange.from && dateRange.to)
    ? Math.max(1, Math.ceil((dateRange.to - dateRange.from) / 86400000))
    : null;
  const avgPerDay = days ? totalNotes / days : null;
  // Notes by unique tech (active diversity)
  const uniqueTechs = new Set(filtered.map(n => n._techName)).size;

  const visible = sorted.slice(0, ROW_LIMIT);
  const truncated = sorted.length > ROW_LIMIT;
  const thStyle = makeThStyle(sortCol);

  return (
    <div>
      <FilterRow>
        <select value={techFilter} onChange={e => setTechFilter(e.target.value)} style={selectStyle}>
          <option value="all">All Techs</option>
          {allTechNames.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <DatePresetSelect value={datePreset} onChange={setDatePreset} />
        <input
          type="text"
          placeholder="Search note text, client, job, tech…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={inputStyle}
        />
      </FilterRow>

      <KPIStrip>
        <KPI label="Notes"            value={totalNotes.toLocaleString()} />
        <KPI label="Unique Techs"     value={uniqueTechs.toLocaleString()} />
        <KPI label="Avg / Day"        value={avgPerDay != null ? avgPerDay.toFixed(1) : "--"} />
      </KPIStrip>

      <TruncationBanner truncated={truncated} total={sorted.length} />

      <TableShell>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
          <thead>
            <tr>
              <th style={thStyle("tech")}   onClick={() => toggleSort("tech")}>Tech{sortArrow(sortCol, sortDir, "tech")}</th>
              <th style={thStyle("date")}   onClick={() => toggleSort("date")}>Date{sortArrow(sortCol, sortDir, "date")}</th>
              <th style={thStyle("client")} onClick={() => toggleSort("client")}>Client / Job{sortArrow(sortCol, sortDir, "client")}</th>
              <th style={thStyle()}>Note</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && <EmptyRow colSpan={4} message="No notes match these filters." />}
            {visible.map(n => (
              <tr key={n.id} style={{ background: C.bg }}>
                <td style={{ ...tdStyle, color: C.accent, fontWeight: 600 }}>{n._techName}</td>
                <td style={{ ...tdStyle, color: C.muted, whiteSpace: "nowrap" }}>
                  {fmtDate(n._createdDate)}
                  {n._createdDate && (
                    <div style={{ fontSize: 10, color: C.dim, fontVariantNumeric: "tabular-nums" }}>
                      {fmtTime(n._createdDate)}
                    </div>
                  )}
                </td>
                <td style={{ ...tdStyle, whiteSpace: "normal", maxWidth: 260 }}>
                  <ClientJobCell clientName={n._clientName} jobNum={n._jobNum} />
                </td>
                <td style={{ ...tdStyle, whiteSpace: "normal", maxWidth: 600, color: C.muted, lineHeight: 1.5 }}>
                  {n.note_type === "timesheet" && (
                    <span style={{
                      fontSize: 10, fontWeight: 600, color: "#06b6d4",
                      background: "rgba(6,182,212,0.10)", padding: "1px 6px",
                      borderRadius: 4, marginRight: 6, whiteSpace: "nowrap",
                    }}>
                      ⏱ timesheet
                    </span>
                  )}
                  {n._message}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableShell>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PROFITABILITY MASTER VIEW
// ════════════════════════════════════════════════════════════════════════════
function ProfitabilityMaster({ enrichedJobs, allTechNames }) {
  const [techFilter, setTechFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("last_90");
  const [marginFilter, setMarginFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState("closed");
  const [sortDir, setSortDir] = useState("desc");

  const dateRange = useMemo(() => getDateRange(datePreset), [datePreset]);

  const filtered = useMemo(() => {
    let result = enrichedJobs;

    if (dateRange.from) result = result.filter(r => r.closedDate && r.closedDate >= dateRange.from);
    if (dateRange.to)   result = result.filter(r => r.closedDate && r.closedDate <= dateRange.to);

    if (techFilter !== "all") result = result.filter(r => r.techName === techFilter);

    if (marginFilter === "high")   result = result.filter(r => r.profitPct >= 50);
    else if (marginFilter === "mid")  result = result.filter(r => r.profitPct >= 35 && r.profitPct < 50);
    else if (marginFilter === "low")  result = result.filter(r => r.profitPct >= 0  && r.profitPct < 35);
    else if (marginFilter === "neg")  result = result.filter(r => r.profitPct < 0);

    if (statusFilter === "qualifying")     result = result.filter(r => r.qualifies);
    else if (statusFilter === "non_qual")  result = result.filter(r => !r.qualifies);
    else if (statusFilter === "paid")      result = result.filter(r => r.status === "paid");
    else if (statusFilter === "outstanding") result = result.filter(r => r.status === "invoiced" || r.status === "partial");

    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(r =>
        (r.clientName || "").toLowerCase().includes(q) ||
        (r.jobTitle || "").toLowerCase().includes(q) ||
        (r.jobNum || "").includes(q) ||
        (r.techName || "").toLowerCase().includes(q)
      );
    }

    return result;
  }, [enrichedJobs, dateRange, techFilter, marginFilter, statusFilter, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av, bv;
      switch (sortCol) {
        case "tech":     av = a.techName || ""; bv = b.techName || ""; break;
        case "closed":   av = a.closedDate?.getTime() || 0; bv = b.closedDate?.getTime() || 0; break;
        case "updated":  av = a.jobUpdatedAt?.getTime() || 0; bv = b.jobUpdatedAt?.getTime() || 0; break;
        case "client":   av = (a.clientName || "").toLowerCase(); bv = (b.clientName || "").toLowerCase(); break;
        case "revenue":  av = a.revenue || 0; bv = b.revenue || 0; break;
        case "profit":   av = a.profit || 0;  bv = b.profit || 0;  break;
        case "margin":   av = a.profitPct || 0; bv = b.profitPct || 0; break;
        case "bonus":    av = a.bonus || 0;   bv = b.bonus || 0;   break;
        default:         return 0;
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortCol, sortDir]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  }

  // KPIs
  const jobs        = filtered.length;
  const totalRev    = filtered.reduce((s, r) => s + (r.revenue || 0), 0);
  const totalProfit = filtered.reduce((s, r) => s + (r.profit  || 0), 0);
  const avgMargin   = filtered.length > 0
    ? filtered.reduce((s, r) => s + (r.profitPct || 0), 0) / filtered.length
    : null;
  const totalBonus  = filtered.filter(r => r.qualifies).reduce((s, r) => s + (r.bonus || 0), 0);
  const qualCount   = filtered.filter(r => r.qualifies).length;

  const visible = sorted.slice(0, ROW_LIMIT);
  const truncated = sorted.length > ROW_LIMIT;
  const thStyle = makeThStyle(sortCol);

  return (
    <div>
      <FilterRow>
        <select value={techFilter} onChange={e => setTechFilter(e.target.value)} style={selectStyle}>
          <option value="all">All Lead Techs</option>
          {allTechNames.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <DatePresetSelect value={datePreset} onChange={setDatePreset} />
        <select value={marginFilter} onChange={e => setMarginFilter(e.target.value)} style={selectStyle}>
          <option value="all">All Margins</option>
          <option value="high">≥ 50% (great)</option>
          <option value="mid">35–50% (ok)</option>
          <option value="low">0–35% (low)</option>
          <option value="neg">Negative</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="all">All Status</option>
          <option value="qualifying">Bonus-Qualifying</option>
          <option value="non_qual">Below Threshold</option>
          <option value="paid">Paid</option>
          <option value="outstanding">Outstanding</option>
        </select>
        <input
          type="text"
          placeholder="Search client, job, #…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={inputStyle}
        />
      </FilterRow>

      <KPIStrip>
        <KPI label="Jobs"          value={jobs.toLocaleString()} />
        <KPI label="Total Revenue" value={dollar(totalRev)} />
        <KPI label="Total Profit"  value={dollar(totalProfit)} color={totalProfit >= 0 ? C.green : C.red} />
        <KPI label="Avg Margin"    value={pct(avgMargin)}
             color={avgMargin == null ? C.muted : avgMargin >= 50 ? C.green : avgMargin >= 35 ? C.yellow : C.red} />
        <KPI label={`Bonus (${qualCount})`} value={dollar(totalBonus)} color={C.accent} />
      </KPIStrip>

      <TruncationBanner truncated={truncated} total={sorted.length} />

      <TableShell>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
          <thead>
            <tr>
              <th style={thStyle("tech")}    onClick={() => toggleSort("tech")}>Lead{sortArrow(sortCol, sortDir, "tech")}</th>
              <th style={thStyle("closed")}  onClick={() => toggleSort("closed")}>Closed{sortArrow(sortCol, sortDir, "closed")}</th>
              <th style={thStyle("updated")} onClick={() => toggleSort("updated")}>Updated{sortArrow(sortCol, sortDir, "updated")}</th>
              <th style={thStyle("client")}  onClick={() => toggleSort("client")}>Client / Job{sortArrow(sortCol, sortDir, "client")}</th>
              <th style={{ ...thStyle("revenue"), textAlign: "right" }} onClick={() => toggleSort("revenue")}>Revenue{sortArrow(sortCol, sortDir, "revenue")}</th>
              <th style={{ ...thStyle("profit"),  textAlign: "right" }} onClick={() => toggleSort("profit")}>Profit{sortArrow(sortCol, sortDir, "profit")}</th>
              <th style={{ ...thStyle("margin"),  textAlign: "right" }} onClick={() => toggleSort("margin")}>Margin{sortArrow(sortCol, sortDir, "margin")}</th>
              <th style={{ ...thStyle("bonus"),   textAlign: "right" }} onClick={() => toggleSort("bonus")}>Bonus{sortArrow(sortCol, sortDir, "bonus")}</th>
              <th style={thStyle()}>Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && <EmptyRow colSpan={9} message="No jobs match these filters." />}
            {visible.map(r => {
              const daysSince = r.jobUpdatedAt
                ? Math.floor((Date.now() - r.jobUpdatedAt.getTime()) / 86400000)
                : null;
              const updatedColor = daysSince == null
                ? C.muted
                : daysSince <= 60 ? C.green
                : daysSince <= 90 ? C.yellow
                : C.muted;
              const marginColor = r.profitPct >= 50 ? C.green : r.profitPct >= 35 ? C.yellow : r.profitPct >= 0 ? C.red : C.red;
              return (
                <tr key={`${r.jobNum}-${r.techFirst}`} style={{ background: C.bg }}>
                  <td style={{ ...tdStyle, color: C.accent, fontWeight: 600 }}>{r.techName || "—"}</td>
                  <td style={{ ...tdStyle, color: C.muted }}>{fmtDate(r.closedDate)}</td>
                  <td style={tdStyle}>
                    <span style={{ color: updatedColor, fontWeight: 600 }}>{fmtDate(r.jobUpdatedAt)}</span>
                    {daysSince != null && (
                      <div style={{ fontSize: 10, color: C.dim }}>
                        {daysSince === 0 ? "today" : daysSince === 1 ? "1d ago" : `${daysSince}d ago`}
                      </div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: "normal", maxWidth: 280 }}>
                    <ClientJobCell clientName={r.clientName} jobTitle={r.jobTitle} jobNum={r.jobNum} />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{dollar(r.revenue)}</td>
                  <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", color: r.profit >= 0 ? C.text : C.red }}>{dollar(r.profit)}</td>
                  <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", color: marginColor, fontWeight: 600 }}>{pct(r.profitPct)}</td>
                  <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", color: r.qualifies ? C.accent : C.dim }}>
                    {r.qualifies ? dollar(r.bonus) : "—"}
                  </td>
                  <td style={tdStyle}>
                    {r.status === "paid"      && <Pill color={C.green}  bg={C.greenDim}>Paid</Pill>}
                    {r.status === "partial"   && <Pill color={C.yellow} bg={C.yellowDim}>Partial</Pill>}
                    {r.status === "invoiced"  && <Pill color={C.yellow} bg={C.yellowDim}>Invoiced</Pill>}
                    {r.status === "completed" && <Pill color={C.muted}  bg={`${C.border}40`}>Done</Pill>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableShell>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// REVIEWS MASTER VIEW
// ════════════════════════════════════════════════════════════════════════════
function ReviewsMaster({ enrichedReviews, allTechNames }) {
  const [techFilter, setTechFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("last_90");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [commentFilter, setCommentFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const dateRange = useMemo(() => getDateRange(datePreset), [datePreset]);

  const filtered = useMemo(() => {
    let result = enrichedReviews;

    if (dateRange.from) result = result.filter(r => r._createdDate && r._createdDate >= dateRange.from);
    if (dateRange.to)   result = result.filter(r => r._createdDate && r._createdDate <= dateRange.to);

    if (techFilter !== "all") {
      result = result.filter(r => r._creditTechs.includes(techFilter));
    }

    if (ratingFilter === "5")    result = result.filter(r => r.rating === 5);
    else if (ratingFilter === "4")    result = result.filter(r => r.rating === 4);
    else if (ratingFilter === "3")    result = result.filter(r => r.rating === 3);
    else if (ratingFilter === "low")  result = result.filter(r => r.rating != null && r.rating <= 2);

    if (commentFilter === "with") result = result.filter(r => r._hasComment);
    else if (commentFilter === "without") result = result.filter(r => !r._hasComment);

    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(r =>
        (r.comment || "").toLowerCase().includes(q) ||
        (r.reviewer_name || "").toLowerCase().includes(q) ||
        (r._clientName || "").toLowerCase().includes(q) ||
        (r._creditTechs || []).some(t => t.toLowerCase().includes(q))
      );
    }

    return result;
  }, [enrichedReviews, dateRange, techFilter, ratingFilter, commentFilter, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av, bv;
      switch (sortCol) {
        case "date":     av = a._createdDate?.getTime() || 0; bv = b._createdDate?.getTime() || 0; break;
        case "rating":   av = a.rating || 0; bv = b.rating || 0; break;
        case "reviewer": av = (a.reviewer_name || "").toLowerCase(); bv = (b.reviewer_name || "").toLowerCase(); break;
        default:         return 0;
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortCol, sortDir]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  }

  const ratedReviews = filtered.filter(r => r.rating != null);
  const avgRating = ratedReviews.length > 0
    ? ratedReviews.reduce((s, r) => s + r.rating, 0) / ratedReviews.length
    : null;
  const fiveStarCount = filtered.filter(r => r.rating === 5).length;
  const lowStarCount  = filtered.filter(r => r.rating != null && r.rating <= 2).length;

  const visible = sorted.slice(0, ROW_LIMIT);
  const truncated = sorted.length > ROW_LIMIT;
  const thStyle = makeThStyle(sortCol);

  return (
    <div>
      <FilterRow>
        <select value={techFilter} onChange={e => setTechFilter(e.target.value)} style={selectStyle}>
          <option value="all">All Credited Techs</option>
          {allTechNames.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <DatePresetSelect value={datePreset} onChange={setDatePreset} />
        <select value={ratingFilter} onChange={e => setRatingFilter(e.target.value)} style={selectStyle}>
          <option value="all">All Ratings</option>
          <option value="5">5 stars</option>
          <option value="4">4 stars</option>
          <option value="3">3 stars</option>
          <option value="low">≤ 2 stars</option>
        </select>
        <select value={commentFilter} onChange={e => setCommentFilter(e.target.value)} style={selectStyle}>
          <option value="all">All Reviews</option>
          <option value="with">With Comment</option>
          <option value="without">No Comment</option>
        </select>
        <input
          type="text"
          placeholder="Search comment, reviewer, client, tech…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={inputStyle}
        />
      </FilterRow>

      <KPIStrip>
        <KPI label="Reviews"      value={filtered.length.toLocaleString()} />
        <KPI label="Avg Rating"   value={avgRating != null ? avgRating.toFixed(2) : "--"}
             color={avgRating == null ? C.muted : avgRating >= 4.5 ? C.green : avgRating >= 4 ? C.yellow : C.red} />
        <KPI label="5-Star"       value={fiveStarCount.toLocaleString()} color={C.green} />
        <KPI label="≤ 2-Star"     value={lowStarCount.toLocaleString()}  color={lowStarCount > 0 ? C.red : C.muted} />
      </KPIStrip>

      <TruncationBanner truncated={truncated} total={sorted.length} />

      <TableShell>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
          <thead>
            <tr>
              <th style={thStyle("date")}     onClick={() => toggleSort("date")}>Date{sortArrow(sortCol, sortDir, "date")}</th>
              <th style={thStyle("rating")}   onClick={() => toggleSort("rating")}>Rating{sortArrow(sortCol, sortDir, "rating")}</th>
              <th style={thStyle("reviewer")} onClick={() => toggleSort("reviewer")}>Reviewer{sortArrow(sortCol, sortDir, "reviewer")}</th>
              <th style={thStyle()}>Credited</th>
              <th style={thStyle()}>Client / Job</th>
              <th style={thStyle()}>Comment</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && <EmptyRow colSpan={6} message="No reviews match these filters." />}
            {visible.map(r => (
              <tr key={r.id} style={{ background: C.bg }}>
                <td style={{ ...tdStyle, color: C.muted }}>{fmtDate(r._createdDate)}</td>
                <td style={tdStyle}><Stars rating={r.rating} /></td>
                <td style={{ ...tdStyle, color: C.text }}>{r.reviewer_name || "—"}</td>
                <td style={{ ...tdStyle, whiteSpace: "normal", maxWidth: 200 }}>
                  {r._creditTechs.length === 0
                    ? <span style={{ color: C.dim, fontStyle: "italic" }}>unassigned</span>
                    : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {r._creditTechs.map(t => (
                          <span key={t} style={{
                            fontSize: 11, padding: "2px 8px", borderRadius: 4,
                            color: C.accent, background: C.accentDim, fontWeight: 500,
                          }}>{t}</span>
                        ))}
                      </div>
                    )}
                </td>
                <td style={{ ...tdStyle, whiteSpace: "normal", maxWidth: 220 }}>
                  <ClientJobCell clientName={r._clientName} jobNum={r._jobNum} />
                </td>
                <td style={{ ...tdStyle, whiteSpace: "normal", maxWidth: 500, color: C.muted, lineHeight: 1.5, fontStyle: r._hasComment ? "normal" : "italic" }}>
                  {r._hasComment
                    ? r.comment
                    : <span style={{ color: C.dim }}>(no comment)</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableShell>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SHARED PRESENTATION HELPERS
// ════════════════════════════════════════════════════════════════════════════
function FilterRow({ children }) {
  return (
    <div style={{
      display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
      marginBottom: 14,
    }}>
      {children}
    </div>
  );
}

function KPIStrip({ children }) {
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
      {children}
    </div>
  );
}

function KPI({ label, value, color }) {
  return (
    <div style={{
      flex: "1 1 140px", minWidth: 140,
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 10, padding: "10px 14px",
    }}>
      <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || C.text, marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

function TruncationBanner({ truncated, total }) {
  if (!truncated) return null;
  return (
    <div style={{
      padding: "10px 14px", marginBottom: 10,
      background: C.yellowDim, border: `1px solid ${C.yellow}40`,
      borderRadius: 8, fontSize: 12, color: C.yellow,
    }}>
      Showing the top {ROW_LIMIT.toLocaleString()} of {total.toLocaleString()} matches.
      Refine filters to narrow the result.
    </div>
  );
}

function TableShell({ children }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 10, overflow: "hidden",
    }}>
      <div style={{ overflowX: "auto", maxHeight: "70vh", overflowY: "auto" }}>
        {children}
      </div>
    </div>
  );
}

function EmptyRow({ colSpan, message }) {
  return (
    <tr>
      <td colSpan={colSpan} style={{
        ...tdStyle, textAlign: "center", color: C.muted, padding: "24px 12px",
      }}>
        {message}
      </td>
    </tr>
  );
}

function Pill({ color, bg, children }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4,
      color, background: bg,
      textTransform: "uppercase", letterSpacing: "0.05em",
      marginRight: 4,
    }}>
      {children}
    </span>
  );
}

function ClientJobCell({ clientName, jobTitle, jobNum }) {
  return (
    <>
      <div style={{ color: C.text, fontWeight: 500 }}>
        {clientName || jobTitle || (jobNum ? `Job #${jobNum}` : "—")}
      </div>
      {clientName && jobTitle && jobTitle !== clientName && (
        <div style={{ fontSize: 11, color: C.muted }}>{jobTitle}</div>
      )}
      {jobNum && (
        <a
          href={`https://secure.getjobber.com/jobs?search=${encodeURIComponent(jobNum)}`}
          target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 11, color: C.dim, textDecoration: "none" }}
        >
          #{jobNum} →
        </a>
      )}
    </>
  );
}

function Stars({ rating }) {
  if (rating == null) return <span style={{ color: C.dim }}>—</span>;
  const filled = Math.max(0, Math.min(5, rating));
  return (
    <span style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "0.05em" }}>
      <span style={{ color: filled >= 4 ? C.green : filled >= 3 ? C.yellow : C.red }}>
        {"★".repeat(filled)}
      </span>
      <span style={{ color: C.dim }}>{"★".repeat(5 - filled)}</span>
    </span>
  );
}

function DatePresetSelect({ value, onChange }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={selectStyle}>
      {DATE_PRESET_OPTIONS.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}
